import { env } from "@/config/env";
import { TOKEN_STORAGE_KEY, GUEST_TOKEN_KEY } from "@/config/base";

const DEFAULT_HEADERS: Record<string, string> = {
  Accept: "application/json",
};

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface HttpRequestConfig {
  headers?: Record<string, string>;
  query?: QueryParams;
  signal?: AbortSignal;
  credentials?: RequestCredentials;
  skipAuthRedirect?: boolean;
  authToken?: string; // Overrides the default token from localStorage
}

export interface HttpRequestOptions<TBody> extends HttpRequestConfig {
  method: HttpMethod;
  body?: TBody;
}

export class HttpError<T = unknown> extends Error {
  readonly status: number;
  readonly data: T;

  constructor(status: number, message: string, data: T) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

/**
 * Obtém o token do usuário armazenado no localStorage
 */
const getUserToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error("Erro ao obter token do localStorage:", error);
    return null;
  }
};

/**
 * Define o token do usuário no localStorage
 * @deprecated Use diretamente localStorage.setItem(TOKEN_STORAGE_KEY, token)
 */
export const setHttpClientUserToken = (token: string | null) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch (error) {
    console.error("Erro ao salvar token no localStorage:", error);
  }
};

/**
 * @deprecated Use setHttpClientUserToken
 */
export const revalidateApiAccessToken = (token: string | null) => {
  setHttpClientUserToken(token);
};

export const getGuestToken = (): string | null =>
  sessionStorage.getItem(GUEST_TOKEN_KEY);

export const setGuestToken = (token: string | null) => {
  if (token) sessionStorage.setItem(GUEST_TOKEN_KEY, token);
  else sessionStorage.removeItem(GUEST_TOKEN_KEY);
};

const FALLBACK_BASE_URL = "http://localhost";

const normalizedBaseUrl = (env.VITE_API_URL || FALLBACK_BASE_URL).replace(
  /\/$/,
  ""
);
const BASE_URL = `${normalizedBaseUrl}`.replace(/\/$/, "");

const toSearchParams = (query?: QueryParams) => {
  if (!query) {
    return "";
  }

  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    params.set(key, String(value));
  });

  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
};

const buildUrl = (path: string, query?: QueryParams) => {
  const normalizedBase = BASE_URL.replace(/\/$/, "");
  const isAbsolute = /^https?:\/\//i.test(path);

  if (isAbsolute) {
    const url = new URL(path);
    if (query) {
      const params = new URLSearchParams(url.search);
      Object.entries(query).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }
        params.set(key, String(value));
      });
      url.search = params.toString();
    }
    return url.toString();
  }

  const formattedPath = path.startsWith("/") ? path : `/${path}`;
  const base = normalizedBase || FALLBACK_BASE_URL;
  const url = new URL(`${base}${formattedPath}${toSearchParams(query)}`);
  return url.toString();
};

const isJsonBody = (
  body: unknown
): body is Record<string, unknown> | unknown[] =>
  body !== null &&
  typeof body === "object" &&
  !(body instanceof FormData) &&
  !(body instanceof Blob);

async function request<TResponse, TBody = unknown>(
  path: string,
  {
    method,
    body,
    headers,
    query,
    signal,
    credentials,
    skipAuthRedirect,
    authToken,
  }: HttpRequestOptions<TBody>
): Promise<TResponse> {
  const url = buildUrl(path, query);

  const mergedHeaders = new Headers({
    ...DEFAULT_HEADERS,
    ...headers,
  });

  const currentToken = authToken ?? getUserToken();
  if (currentToken && !mergedHeaders.has("Authorization")) {
    mergedHeaders.set("Authorization", `Bearer ${currentToken}`);
  }

  const config: RequestInit = {
    method,
    signal,
    credentials,
    headers: mergedHeaders,
  };

  if (body !== undefined) {
    if (body instanceof FormData || body instanceof Blob) {
      config.body = body as BodyInit;
    } else if (isJsonBody(body)) {
      mergedHeaders.set("Content-Type", "application/json");
      config.body = JSON.stringify(body);
    } else {
      config.body = body as BodyInit;
    }
  }

  try {
    const response = await fetch(url, config);
    const contentType = response.headers.get("content-type") ?? "";
    let data: unknown = null;

    if (contentType.includes("application/json")) {
      data = await response.json();
    } else if (contentType.includes("text/")) {
      data = await response.text();
    }

    if (!response.ok) {
      const errorMessage =
        typeof data === "object" && data && "message" in data
          ? String((data as { message: unknown }).message)
          : response.statusText || "HTTP request failed";

      if (response.status === 401) {
        if (!skipAuthRedirect) {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem("user");
          window.location.href = "/auth/identify";
        }
      }

      throw new HttpError(response.status, errorMessage, data);
    }

    return data as TResponse;
  } catch (error) {
    if (error instanceof HttpError) {
      throw error;
    }

    throw error;
  }
}

export interface HttpClient {
  get<T>(path: string, config?: HttpRequestConfig): Promise<T>;
  post<T, B = unknown>(
    path: string,
    body?: B,
    config?: HttpRequestConfig
  ): Promise<T>;
  put<T, B = unknown>(
    path: string,
    body?: B,
    config?: HttpRequestConfig
  ): Promise<T>;
  patch<T, B = unknown>(
    path: string,
    body?: B,
    config?: HttpRequestConfig
  ): Promise<T>;
  delete<T, B = unknown>(
    path: string,
    body?: B,
    config?: HttpRequestConfig
  ): Promise<T>;
}

const createHttpClient = (): HttpClient => ({
  get: (path, config) => request(path, { method: "GET", ...(config ?? {}) }),
  post: (path, body, config) =>
    request(path, { method: "POST", body, ...(config ?? {}) }),
  put: (path, body, config) =>
    request(path, { method: "PUT", body, ...(config ?? {}) }),
  patch: (path, body, config) =>
    request(path, { method: "PATCH", body, ...(config ?? {}) }),
  delete: (path, body, config) =>
    request(path, { method: "DELETE", body, ...(config ?? {}) }),
});

export const httpClient = createHttpClient();
export { createHttpClient };
